import type { ReportStatus } from '@brightlocal/shared';
import { INSIGHTS_STATUS, REPORT_STATUS, TOOL_CODES } from '@brightlocal/shared';

/** Tools whose reports are created by the report set-up flows; used as Sentry tags and GA labels. */
export const REPORT_CREATE_TOOLS = {
  LSG: TOOL_CODES.LSG,
  CT: TOOL_CODES.CT,
  RM: TOOL_CODES.RM,
  LRT: TOOL_CODES.LRT,
  GBPA: TOOL_CODES.GBPA,
  AI_VISIBILITY: TOOL_CODES.AI_VISIBILITY
} as const;

/** Union of the report-creation tool keys. */
export type ReportCreateTool = (typeof REPORT_CREATE_TOOLS)[keyof typeof REPORT_CREATE_TOOLS];

/** API steps of a report creation; a failure is tagged with the step that threw. */
export const REPORT_CREATE_STEPS = {
  /** Data needed before the create call (e.g. resolving the business category). */
  PREPARE: 'prepare',
  /** Starting the profile lookup an RM report's directories come from. */
  LOOKUP: 'lookup',
  /** Waiting for that lookup to end. */
  LOOKUP_WAIT: 'lookup_wait',
  CREATE: 'create',
  RUN: 'run'
} as const;

/** Union of the report-creation step keys. */
export type ReportCreateStep = (typeof REPORT_CREATE_STEPS)[keyof typeof REPORT_CREATE_STEPS];

/** Interval in milliseconds between status polls (reports, insights, AI Visibility). */
export const STATUS_POLLING_INTERVAL_MS = 15_000;

/** Interval in milliseconds between reads of a running RM profile lookup. */
export const RM_PROFILE_LOOKUP_POLL_INTERVAL_MS = 5_000;

/**
 * Reads of a running RM profile lookup before this visit stops waiting (5 minutes, 5 s apart). A lookup
 * still running by then is picked up again on the next app load; the backend fails one only after an hour.
 */
export const RM_PROFILE_LOOKUP_MAX_POLLS = 60;

/** Consecutive failed status requests before polling gives up. */
export const STATUS_POLLING_MAX_CONSECUTIVE_FAILURES = 15;

/**
 * Consecutive 404s from the AI Insights status endpoint before polling stops. The backend creates the
 * insight a few seconds after LSG/CT report `ok`, so the first poll can land before it exists.
 */
export const INSIGHTS_STATUS_MAX_NOT_FOUND_RESPONSES = 3;

/** Report statuses that indicate a report run is still in progress. */
export const RUNNING_REPORT_STATUSES: ReportStatus[] = [REPORT_STATUS.RUNNING, REPORT_STATUS.FIRST_RUNNING];

/** GTM `eventAction` of a report page's state event (Review Tracker's `rm_status`, Local Rank Tracker's `report state shown`). */
export const REPORT_STATE_ACTION = {
  COMPLETED: 'completed',
  IN_PROGRESS: 'in_progress',
  TO_BE_GENERATED: 'to_be_generated',
  FAILED: 'failed',
  NOT_SET_UP: 'not_set_up'
} as const;

/** Union of the report state actions. */
export type ReportStateAction = (typeof REPORT_STATE_ACTION)[keyof typeof REPORT_STATE_ACTION];

/** State action of each report status, on a screen that shows the report. */
export const REPORT_STATE_ACTION_BY_STATUS = {
  [REPORT_STATUS.OK]: REPORT_STATE_ACTION.COMPLETED,
  [REPORT_STATUS.FIRST_RUNNING]: REPORT_STATE_ACTION.IN_PROGRESS,
  [REPORT_STATUS.RUNNING]: REPORT_STATE_ACTION.IN_PROGRESS,
  [REPORT_STATUS.FAILED]: REPORT_STATE_ACTION.FAILED,
  [REPORT_STATUS.FIRST_RUN_FAILED]: REPORT_STATE_ACTION.FAILED,
  [REPORT_STATUS.NEVER_RUN]: REPORT_STATE_ACTION.TO_BE_GENERATED,
  [REPORT_STATUS.GET_STARTED]: REPORT_STATE_ACTION.TO_BE_GENERATED,
  [REPORT_STATUS.NOT_SETUP]: REPORT_STATE_ACTION.NOT_SET_UP
} as const satisfies Record<ReportStatus, ReportStateAction>;

/** Final statuses for AI Insights generation; polling stops when reached. */
export const AI_INSIGHTS_TERMINAL_STATUSES = [INSIGHTS_STATUS.COMPLETED, INSIGHTS_STATUS.FAILED] as const;

/** Query key for AI Insights status polling. */
export const INSIGHTS_STATUS_QUERY_KEY = 'insightsStatus';

/** Query key for AI Visibility run-status polling. */
export const AI_VISIBILITY_STATUS_QUERY_KEY = 'aiVisibilityStatus';

/** Only the newest run is needed to derive AI Visibility run status. */
export const AI_VISIBILITY_RUNS_FETCH_LIMIT = 1;
